import { SUMMARY_TOP_PRODUCTS, type ClosingGridMode } from "@/types/production-report.types";
import type { ReportBucket, ReportStage } from "@/types/production-report.types";

// Spec 083 — as COLUNAS da grade de fechamento, decididas num lugar só.
//
// A grade e a exportação precisam concordar coluna a coluna: o gestor exporta o que está na tela, e
// um arquivo com colunas diferentes das que ele acabou de conferir é pior que nenhum arquivo.
//
// ⚠️ O modo resumido agrega "os demais" POR VOLUME, e não por família de produto. A plataforma não
// sabe que 1 kg, 500 g e 250 g são "vácuo", e fingir que sabe criaria um conceito que ninguém
// cadastrou — exatamente o que a Spec 082 recusou ao manter o vocabulário do cliente fora do modelo.

export interface ClosingColumn {
  key: string;
  label: string;
  /** Valor do agrupamento; `null` vira travessão, nunca zero. */
  value: (bucket: ReportBucket) => number | null;
  numeric: boolean;
}

const sum = (items: Array<{ quantity: number }>): number | null =>
  items.length === 0 ? null : items.reduce((acc, i) => acc + i.quantity, 0);

function heroOf(bucket: ReportBucket, heroId: string | null) {
  return bucket.indicators.find((i) => i.id === heroId) ?? bucket.indicators[0] ?? null;
}

/** Produtos que ganham coluna própria no modo resumido: os de maior volume NO PERÍODO. */
function topProducts(stage: ReportStage): string[] {
  return stage.products.slice(0, SUMMARY_TOP_PRODUCTS).map((p) => p.productId);
}

export function closingColumns(
  stage: ReportStage,
  mode: ClosingGridMode,
  heroId: string | null
): ClosingColumn[] {
  const cols: ClosingColumn[] = [
    {
      key: "input",
      label: "Entrada",
      numeric: true,
      value: (b) => b.input.quantity,
    },
    {
      key: "output",
      label: "Saída útil",
      numeric: true,
      value: (b) => heroOf(b, heroId)?.numerator ?? null,
    },
  ];

  if (mode === "full") {
    for (const p of stage.products) {
      cols.push({
        key: `p:${p.productId}`,
        label: p.productName,
        numeric: true,
        value: (b) => b.products.find((x) => x.productId === p.productId)?.quantity ?? null,
      });
    }
    for (const l of stage.losses) {
      cols.push({
        key: `l:${l.productId ?? ""}:${l.lossType ?? ""}`,
        label: `Perda ${l.productName ?? "sem produto"}`,
        numeric: true,
        value: (b) =>
          b.losses.find((x) => x.productId === l.productId && x.lossType === l.lossType)
            ?.quantity ?? null,
      });
    }
  } else {
    const top = topProducts(stage);
    for (const id of top) {
      const nome = stage.products.find((p) => p.productId === id)?.productName ?? "";
      cols.push({
        key: `p:${id}`,
        label: nome,
        numeric: true,
        value: (b) => b.products.find((x) => x.productId === id)?.quantity ?? null,
      });
    }
    if (stage.products.length > top.length) {
      cols.push({
        key: "others",
        label: "Outros",
        numeric: true,
        value: (b) => sum(b.products.filter((x) => !top.includes(x.productId))),
      });
    }
    cols.push({
      key: "scrap",
      label: "Refugo",
      numeric: true,
      value: (b) => sum(b.losses.filter((l) => l.lossType !== "REWORK")),
    });
  }

  cols.push(
    {
      key: "break",
      label: "Quebra",
      numeric: true,
      value: (b) => {
        const h = heroOf(b, heroId);
        if (!h || h.denominator === null || h.numerator === null) return null;
        return h.denominator - h.numerator;
      },
    },
    {
      key: "indicator",
      label: heroOf(stage.buckets[0] ?? ({ indicators: [] } as never), heroId)?.name ?? "%",
      numeric: true,
      value: (b) => heroOf(b, heroId)?.value ?? null,
    }
  );

  return cols;
}
